import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { isMobileNavbar } from '@services/utils.service';
import { setNavbarMobileMenuOpen } from '@App/components/Navbar/NavbarMobileMenu/navbar-mobile-menu.slice';
import { trackEvent } from '@services/analytics.service';

import GuidedTourTooltip from './GuidedTourTooltip/GuidedTourTooltip';
import { 
  setTourGuideState, 
  setTourStepIndex,
  setSearchBarExpanded, 
  setRiskDatapanelExpanded, 
  setAdaptationDatapanelExpanded, 
} from './guided-tour.slice';

import {
  styles, prepareAdaptationSteps,
  prepareLearnSteps,
  prepareRiskSteps,
  TOUR_STEP_IDS,
} from './guided-tour';

function GuidedTour() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startGuidedTour = useSelector(state => state.guidedTour.isTourGuideOpen)
  const typeOfTour = useSelector(state => state.guidedTour.typeOfTour)
  const stepIndex = useSelector(state => state.guidedTour.stepIndex)
  const breakpoint = useSelector(state => state.windowObserver.breakpoint)
  const isSmallScreen = ['XXXS', 'XXS', 'XS', 'SM'].includes(breakpoint?.name)
  const learnSteps = prepareLearnSteps(t, { breakpoint });
  const riskSteps = prepareRiskSteps(t, { isSmallScreen, breakpoint });
  const adaptationSteps = prepareAdaptationSteps(t, isSmallScreen);
  let step = adaptationSteps;
  if (typeOfTour === 'learn') {
    step = learnSteps;
  } else if (typeOfTour === 'risks') {
    step = riskSteps;
  }

  const activeStepId = step[stepIndex]?.data?.stepId;
  const learnMobileMenuStepSelectors = {
    [TOUR_STEP_IDS.learn.search]: '#navbar-mobile-menu__search-button',
    [TOUR_STEP_IDS.learn.backToExplore]: '.my-sixth-learn-step',
  };
  const isLearnMobileMenuStep = stepId => Object.hasOwn(learnMobileMenuStepSelectors, stepId);

  const getStepIndexById = stepId => step.findIndex(tourStep => tourStep.data?.stepId === stepId);

  const setTourStepById = (stepId) => {
    const nextStepIndex = getStepIndexById(stepId);

    if (nextStepIndex >= 0) {
      dispatch(setTourStepIndex(nextStepIndex));
    }
  };

  useEffect(() => {
    if (startGuidedTour && typeOfTour) {
      trackEvent(`guided_tour_${typeOfTour}_started`);
    }
  }, [startGuidedTour, typeOfTour]);

  useEffect(() => {
    if (!startGuidedTour) {
      dispatch(setTourStepIndex(0));
    }
  }, [startGuidedTour, dispatch])

  useEffect(() => {
    if (!startGuidedTour || !activeStepId) {
      return;
    }

    if (typeOfTour === 'learn' && isMobileNavbar(breakpoint)) {
      dispatch(setNavbarMobileMenuOpen(isLearnMobileMenuStep(activeStepId)));
    }

    if (typeOfTour === 'risks') {
      const isMapFocusedRiskStep = [
        TOUR_STEP_IDS.risks.mapOverview,
        TOUR_STEP_IDS.risks.mapLegend,
        TOUR_STEP_IDS.risks.backgroundMap,
      ].includes(activeStepId);

      dispatch(setRiskDatapanelExpanded(!isSmallScreen || !isMapFocusedRiskStep));
    }

    if (typeOfTour === 'adaptation') {
      const isFullscreenDatapanel = breakpoint?.maxWidth < 768;

      const isMapFocusedAdaptationStep = [
        TOUR_STEP_IDS.adaptation.mapControls,
      ].includes(activeStepId);

      dispatch(setAdaptationDatapanelExpanded(!isFullscreenDatapanel || !isMapFocusedAdaptationStep));
    }
  }, [activeStepId, dispatch, startGuidedTour, typeOfTour, isSmallScreen, breakpoint]);

  const waitForElement = (selector, onFound, maxAttempts = 20, interval = 100) => {
    let attempts = 0;
    const timer = setInterval(() => {
      const element = document.querySelector(selector);
      if (element) {
        clearInterval(timer);
        onFound();
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        clearInterval(timer);
      }
    }, interval);
  };

  const handleJoyrideCallback = (event) => {
    const {
      action, index, type, status, step: currentStep,
    } = event;

    const currentStepId = currentStep?.data?.stepId;

    if (typeOfTour === 'risks' && currentStepId === TOUR_STEP_IDS.risks.tabs && action === ACTIONS.NEXT) {
      const tabElement = document.querySelector('.risk-data-content__temperature');
      if (tabElement) {
        tabElement.click();
      }
    }

    if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.mapControls && action === ACTIONS.NEXT) {
      const tabElement = document.querySelector('.my-seventh-adaptation-step');
      if (tabElement) {
        tabElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }

    if (action === ACTIONS.CLOSE) {
      dispatch(setNavbarMobileMenuOpen(false));
      dispatch(setTourGuideState(false));
      dispatch(setSearchBarExpanded(false));
      dispatch(setTourStepIndex(0));
      // callback for when the popup gets closed using the 'x'
    }
    
    if (action === ACTIONS.PREV) {
      // callback for going to the previous step
    }

    if (([STATUS.FINISHED, STATUS.SKIPPED]).includes(status)) {
      // callback for when the entire tour has been completed
      dispatch(setNavbarMobileMenuOpen(false));
      dispatch(setTourGuideState(false));
      dispatch(setTourStepIndex(0));
      dispatch(setSearchBarExpanded(false));
    } 

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if (type === EVENTS.TARGET_NOT_FOUND) {
        if (typeOfTour === 'learn' && isMobileNavbar(breakpoint) && isLearnMobileMenuStep(currentStepId)) {
          dispatch(setNavbarMobileMenuOpen(true));
          waitForElement(learnMobileMenuStepSelectors[currentStepId], () => {
            setTourStepById(currentStepId);
          });
          return;
        }

        if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.addPlan) {
          waitForElement('.my-fifth-adaptation-step', () => {
            setTourStepById(TOUR_STEP_IDS.adaptation.addPlan);
          }, 80, 100);
          return;
        }

        return;
      }

      if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.addEntry && action === ACTIONS.NEXT) {
        navigate('/adaptation/plans/create');
        waitForElement('.my-fifth-adaptation-step', () => {
          setTourStepById(TOUR_STEP_IDS.adaptation.addPlan);
        }, 80, 100);
        return;
      }

      if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.addPlan && action === ACTIONS.PREV) {
        navigate('/adaptation/plans');
        waitForElement('.my-fourth-adaptation-step', () => {
          setTourStepById(TOUR_STEP_IDS.adaptation.addEntry);
        }, 80, 100);
        return;
      }

      if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.mapControls && action === ACTIONS.NEXT) {
        setTimeout(() => {
          dispatch(setTourStepIndex(index + 1));
        }, 700);
        return;
      }

      if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.submitPlan && action === ACTIONS.NEXT) {
        navigate('/adaptation/stories/create');
        waitForElement('.my-eighth-adaptation-step', () => {
          setTourStepById(TOUR_STEP_IDS.adaptation.addStory);
          dispatch(setTourGuideState(true));
        });

        return;
      }

      if (typeOfTour === 'adaptation' && currentStepId === TOUR_STEP_IDS.adaptation.addStory && action === ACTIONS.PREV) {
        navigate('/adaptation/plans/create');
        waitForElement('.my-seventh-adaptation-step', () => {
          setTourStepById(TOUR_STEP_IDS.adaptation.submitPlan);
        }, 80, 100);
        return;
      }

      if (typeOfTour === 'learn' && isMobileNavbar(breakpoint)) {
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        const nextStepId = step[nextStepIndex]?.data?.stepId;

        if (isLearnMobileMenuStep(nextStepId)) {
          dispatch(setNavbarMobileMenuOpen(true));
          waitForElement(learnMobileMenuStepSelectors[nextStepId], () => {
            dispatch(setTourStepIndex(nextStepIndex));
          });
          return;
        }

        if (isLearnMobileMenuStep(currentStepId)) {
          dispatch(setNavbarMobileMenuOpen(false));
        }
      }

      dispatch(setTourStepIndex(index + (action === ACTIONS.PREV ? -1 : 1)));
    }
  }

  return (
    <div className="guided-tour">
      <Joyride
        run={startGuidedTour}
        steps={step}
        stepIndex={stepIndex}
        callback={event => handleJoyrideCallback(event)}
        continuous
        controlled
        showProgress
        showSkipButton
        disableBeacon
        disableScrolling
        scrollToFirstStep={false}
        styles={styles}
        tooltipComponent={GuidedTourTooltip}
        spotlightPadding={6} 
      />   
    </div>
  );
}

export default GuidedTour;
